import React from 'react';
import { Input } from 'antd';

// Госномер РФ (OilChangePage.md §3В): два независимых буфера — буквы и цифры,
// отображение собирается по шаблону Б999ББ99(9) независимо от порядка ввода.
// Латиница: сначала омоглиф → русская (сохраняет старые/вставленные значения),
// иначе — клавиша раскладки QWERTY→ЙЦУКЕН (f→а, d→в, g→п). Валидны только АВЕКМНОРСТУХ.
// Валидация мягкая: неполный номер — warning-подсветка, сохранение не блокируется.

const HOMOGLYPH: Record<string, string> = {
  A: 'А', B: 'В', C: 'С', E: 'Е', H: 'Н', K: 'К', M: 'М',
  O: 'О', P: 'Р', T: 'Т', X: 'Х', Y: 'У',
};

const KEYBOARD: Record<string, string> = {
  Q: 'Й', W: 'Ц', E: 'У', R: 'К', T: 'Е', Y: 'Н', U: 'Г', I: 'Ш', O: 'Щ', P: 'З',
  A: 'Ф', S: 'Ы', D: 'В', F: 'А', G: 'П', H: 'Р', J: 'О', K: 'Л', L: 'Д',
  Z: 'Я', X: 'Ч', C: 'С', V: 'М', B: 'И', N: 'Т', M: 'Ь',
};

const VALID_LETTERS = new Set('АВЕКМНОРСТУХ');

const toPlateLetter = (ch: string): string | null => {
  const up = ch.toUpperCase();
  if (VALID_LETTERS.has(up)) return up;
  const hom = HOMOGLYPH[up];
  if (hom) return hom;
  const kb = KEYBOARD[up];
  if (kb && VALID_LETTERS.has(kb)) return kb;
  return null;
};

export const assemblePlate = (raw: string): string => {
  const letters: string[] = [];
  const digits: string[] = [];
  for (const ch of raw) {
    if (/\d/.test(ch)) {
      if (digits.length < 6) digits.push(ch);
    } else {
      const l = toPlateLetter(ch);
      if (l && letters.length < 3) letters.push(l);
    }
  }
  return (
    (letters[0] ?? '') +
    digits.slice(0, 3).join('') +
    letters.slice(1, 3).join('') +
    digits.slice(3).join('')
  );
};

const FULL_PLATE_RE = /^[АВЕКМНОРСТУХ]\d{3}[АВЕКМНОРСТУХ]{2}\d{2,3}$/;

interface Props {
  value?: string | null;
  disabled?: boolean;
  onChange: (v: string | null) => void;
}

const PlateInput: React.FC<Props> = ({ value, disabled, onChange }) => {
  const plate = assemblePlate(value ?? '');
  const warn = plate.length > 0 && !FULL_PLATE_RE.test(plate);
  return (
    <Input
      value={plate}
      disabled={disabled}
      placeholder="А123БВ51"
      maxLength={9}
      status={warn ? 'warning' : ''}
      onChange={e => {
        const next = assemblePlate(e.target.value);
        onChange(next || null);
      }}
    />
  );
};

export default PlateInput;
